import {isDevice} from "@atg/utils/device";
import {fetchSelectors} from "@atg-shared/fetch-redux";
import {LOADING_STATUS} from "@atg-shared/fetch-types";

import type {LiveTableType} from "@atg-casino-shared/types-feed";
import type {GameMin} from "@atg-casino-shared/types-game";
import type {FETCH} from "@atg-shared/fetch-redux";
import type {FetchState, LoadingStatus} from "@atg-shared/fetch-types";
import type {Reducer, Action} from "redux";

export type WithoutFetch<T> = Exclude<T, {type: typeof FETCH}>;
type ActionWithContext<Key extends string> = Action & {
    context?: {[K in Key]: string};
};
type State<Entity> = Record<string, Entity>;

export function createEntityFetchReducer<
    Entity,
    Key extends string,
    A extends ActionWithContext<Key>,
>(
    requestAction: string,
    receiveAction: string,
    resetAction: string | null | undefined,
    reducer: Reducer<Entity, A>,
    contextKey: Key,
): Reducer<State<Entity>, A> {
    const getKey = (action: A) => action.context && action.context[contextKey];

    const reduceEntity = (state: State<Entity>, action: A, key?: string) =>
        key && {...state, [key]: reducer(state[key], action)};

    const reduceAll = (state: State<Entity>, action: A) =>
        Object.keys(state).reduce((s, k) => ({...s, [k]: reducer(state[k], action)}), {});

    return (state = {}, action) => {
        switch (action.type) {
            case requestAction:
            case receiveAction:
                return reduceEntity(state, action, getKey(action)) || state;
            case resetAction:
                return resetAction ? reduceAll(state, action) : state;
            default:
                return state;
        }
    };
}

export const getLoadingStatus = <S extends FetchState<unknown>>(
    state: S | null | undefined,
): LoadingStatus =>
    state ? fetchSelectors.getLoadingStatus(state) : {status: LOADING_STATUS.INITIAL};

export const filterUA = ({hideOnDesktop, hideOnDevice}: GameMin) =>
    isDevice() ? !hideOnDevice : !hideOnDesktop;

export const sortAtoZ = (a: string, b: string) => {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
};

export const filteredGameIds = (games: GameMin[]): string[] =>
    games.filter(filterUA).map(({id}) => id);

export const getTablePlayersInfo = (
    liveTableInfo: LiveTableType,
    options?: {shortenSuffix?: boolean},
) => {
    if (!liveTableInfo.open) {
        return "Stängt";
    }
    if (liveTableInfo?.availableSeats !== null) {
        const availableSeatsSuffix = options?.shortenSuffix
            ? "platser"
            : "lediga platser";
        return `${liveTableInfo.availableSeats} ${availableSeatsSuffix}`;
    }
    if (liveTableInfo?.tableIsFull) {
        return "Fullsatt";
    }
    if (liveTableInfo?.playerCount !== null) {
        return `${liveTableInfo.playerCount} spelare`;
    }

    return null;
};

export const iFrameSize = (size: {height: number; width: number} | null | undefined) =>
    size
        ? {
              minHeight: `${size.height}px`, // Required for iOS
              height: `${size.height}px`,
              width: `${size.width}px`,
          }
        : {};
